import React from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ReportFormVM } from '../report-form.viewmodel';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { SPACE_IN_BEGIN_REG_EXP } from '../../../../../../constants/regex.constants';
import UploadButton, { FILE_TYPE } from '../../../shared/upload-button';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { ReportType } from '../../../../../../api/reports.dao';
import { reportTypeLabels } from '../../../../../../constants/report-labels.constants';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import OptionsButton from '../../../../../global/options-button/options-button';
import { ADD_TEXT, APPLY_TEXT, CANCEL_TEXT, MORE_BUTTON_TEXT } from '../../../../../../constants/common-labels.constants';

interface IProps {
    vm: ReportFormVM;
    intl: IntlShape;
}

const ReportFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { intl, vm } = props;
    const formDataVM = vm.getFormDataVM();

    const isEdit = formDataVM.isEdit;

    const formSubmitHandler = () => {
        return vm.handleSubmit(intl);
    };

    const reportOptions = [
        isEdit && hasPermission(Permission.DELETE_REPORT) && {
            label: intl.formatMessage({
                id: 'oe.reports.deleteFile',
                defaultMessage: 'Delete File',
                description: 'Label text'
            }),
            action: () => vm.deleteReport(intl),
            danger: true
        }
    ].filter(Boolean);

    if (vm.isLoading) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form meeting-form'>
                    <FormattedMessage
                        description='Label text'
                        defaultMessage='Loading report data{suffix}'
                        id='oe.reports.loadingData'
                        values={{
                            suffix: '...'
                        }}
                    />
                </div>
            }
        />;
    }

    return (
        <CommonPopupBodyFooterLayout
            body={
                <div className='report-form'>
                    <div className='oe-form'>
                        {<div className='oe-form__body'>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.reports.name',
                                        description: 'Report name title',
                                        defaultMessage: 'Report Name'
                                    })
                                }>
                                <input
                                    type='text'
                                    className='oe-form-field__control'
                                    value={formDataVM.alias}
                                    onChange={({ target: { value } }) => formDataVM.setAlias(value.replace(SPACE_IN_BEGIN_REG_EXP, ''))}
                                />
                                {formDataVM.showValidationErrors() ? <div
                                    className='oe-form-field__error'>{formDataVM.getReportAliasError(intl)}</div> : null}
                                {formDataVM.serverErrorMessage &&
                                <div className='oe-form-field__error'>{formDataVM.serverErrorMessage}</div>}
                            </FormField>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.reports.reportType',
                                        description: 'Report type title',
                                        defaultMessage: 'Report Type'
                                    })
                                }>
                                <select
                                    value={formDataVM.assetType}
                                    onChange={mobx.action(e => formDataVM.assetType = e.target.value as ReportType)}
                                    className='oe-form-field__control'
                                >
                                    {(Object.keys(ReportType) as (keyof typeof ReportType)[]).map(key =>
                                        <option key={key} value={ReportType[key]}>{intl.formatMessage(reportTypeLabels[key])}</option>
                                    )}
                                </select>
                            </FormField>
                            { !isEdit && <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.reports.file',
                                        description: 'Placeholder text',
                                        defaultMessage: 'File'
                                    })
                                }>
                                <UploadButton
                                    fileName={formDataVM.reportFile?.name}
                                    onChange={formDataVM.setReportFile}
                                    uploadedFileType={FILE_TYPE.REPORT}
                                />
                                {formDataVM.showValidationErrors()
                                    ?
                                    <div className='oe-form-field__error'>{formDataVM.getReportFileError(intl)}</div>
                                    : null
                                }
                            </FormField>}
                        </div>
                        }
                    </div>
                </div>
            }
            footer={
                <>
                    {vm.isSaving() &&
                    <span className='oe-popup-layout__footer-status-area'>
                            {intl.formatMessage({
                                id: 'oe.reports.reportIsBeingSaved',
                                description: 'Placeholder about saving report',
                                defaultMessage: 'Report is being saved, please wait...'
                            })}
                        </span>
                    }

                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(isEdit ? APPLY_TEXT : ADD_TEXT)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={formSubmitHandler}
                            disabled={vm.isSaving()}
                        />
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(CANCEL_TEXT)}
                        />
                        {!!reportOptions.length && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={reportOptions}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>
            }
        />
    );

}));

export { ReportFormPresentation };
